import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain for Oriora Journal. SERVER-FIRST: askBrain sends the user's
// message + this app's declared action catalog (CHAT_ACTIONS) + a small metadata context to the
// generic server chat brain (POST /api/apps/solo_journal/chat — ONE billed call, via the same
// exported brainCall as /reflect) which maps it to an ARRAY of actions + a short reply
// ("delete Monday's and Tuesday's entries" = two actions, both run). The params come back
// LOOSE, so toAction() coerces every one defensively (trim/clamp/validate) and resolves
// by-name references to LOCAL row ids (resolveOne → resolveTarget, fuzzy — Pattern 6) before
// anything executes.
//
// FALLBACK: when the server can't be reached (offline / signed out / server trouble) askBrain
// falls back to the ORIGINAL free local regex router below (routeMessage), kept fully intact:
//   • "add entry … / log … / journal …"          → local parse            → add_entry
//   • "show / find / entries about … / mood …"    → local filter           → filter
//   • "reflect / recap my week / how am I doing"  → existing /reflect       → reflect
//   • "delete / remove <entry>"                   → resolve id locally      → delete_entry (UI confirms)
//   • "set <entry>'s mood to N" · "rename X to Y" → resolve id locally      → edit_entry
//   • anything else                               → none + a short clarifying nudge
//
// PRIVACY (pull-not-push): the AMBIENT chat context is metadata ONLY — entry titles, moods and
// dates (the same metadata /reflect already receives). Stored entry BODIES transit ONLY on the
// `recap` action (chat/actions.ts), i.e. only when the user explicitly asks about their own
// writing, once per answer, and Oriora retains nothing. Every action still executes LOCALLY
// against the on-device store, and delete still requires the UI's Confirm tap.

import { BrainError, brainCall } from "../brain";
import { store, type JournalEntry, type EntryPatch } from "../store";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, EntryFilter, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
  noHistory?: boolean; // recap answers quote entry text — never re-sent in the ambient window
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line(s)
  actions: ChatAction[]; // the shared brain returns an ARRAY — multiple asks in one message = one action each
}

// The app's fence, sent to the brain as `scope` (server truncates at 400 chars → hard limits FIRST).
// Tier A+B: full helpfulness on THEIR journal; the one hard line is mental-health advice (Tier C
// substance — decline + professional pointer, exactly that line, no stricter).
export const JOURNAL_SCOPE =
  "HARD LIMITS: no mental-health, therapy, medication, diagnosis or crisis advice — decline that " +
  "plainly and point to a doctor or licensed therapist. Oriora Journal is the user's private personal " +
  "journal (entries, moods, optional photos). Within it, help fully: add/find/edit entries, recap and " +
  "reflect on what THEY wrote — quote their own entries honestly, never invent content they didn't write.";

// ── PRE-LLM DISTRESS FENCE (client, deterministic; shared pattern set with Forever Paw) ──
// A journal is exactly where acute self-harm / crisis phrasing surfaces. If the user's typed
// chat message carries such signals we must NOT route it to a "here's your reflection" AI reply
// (and must not even bill a call) — we surface human support instead. Deliberately narrow
// (acute self-harm phrasing only) so ordinary sad/low journaling — the whole point of the app —
// passes straight through. Whole-word, case-insensitive.
const DISTRESS_PATTERNS: RegExp[] = [
  /\bkill myself\b/i, /\bkilling myself\b/i, /\bend my life\b/i, /\bending my life\b/i,
  /\btake my (own )?life\b/i, /\bwant to die\b/i, /\bdon'?t want to (be alive|live)\b/i,
  /\bno reason to live\b/i, /\bcan'?t go on (without|any longer)\b/i, /\bsuicidal\b/i,
  /\bcommit(?:ting)? suicide\b/i, /\bharm(?:ing)? myself\b/i, /\bhurt(?:ing)? myself\b/i, /\bend(?:ing)?\s+it\s+all\b/i,
  /\b(?:isn'?t|ain'?t|not|no longer)\s+worth\s+living\b/i, /\bnothing\s+to\s+live\s+for\b/i,
];
export function hasDistressText(text: string): boolean {
  return DISTRESS_PATTERNS.some((re) => re.test(text));
}
const CRISIS_MESSAGE =
  "It sounds like you may be going through something very hard. Oriora Journal is your private journal — " +
  "it isn't crisis support. Please reach out to someone who can help right now: in the US call or text 988 " +
  "(Suicide & Crisis Lifeline); elsewhere, find a local line at findahelpline.com.";

const HELP =
  "I can add entries, search them, edit or delete one, recap what you wrote, and reflect on your week — just tell me. Try:\n" +
  '• "add entry Long walk by the river, mood 4"\n' +
  '• "show entries about work" · "happy entries this month"\n' +
  '• "what was on my mind in March?" · "recap this month"\n' +
  '• "reflect on my week" · "back up my journal"\n' +
  '• "set today\'s mood to 5" · "delete yesterday\'s entry"';

// Anything search-shaped routes to the local filter.
const QUERY_RE = /\b(show|find|list|search|which|filter|entries?|entry|about|mentioning|containing|mood|happy|sad|good|bad|great|low|down|this\s+week|last\s+week|this\s+month|last\s+month|today|yesterday|how\s+many)\b/i;

const fmtShort = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Parse a 1-5 mood out of free text ("mood 4", "feeling 5", "3/5", or a mood word).
function parseMood(s: string): number | null {
  let m = s.match(/\b(?:mood|feeling|felt|rate|rating)\s*(?:of|:|=|is|was|to)?\s*([1-5])\b/i)
    || s.match(/\b([1-5])\s*\/\s*5\b/);
  if (m) return parseInt(m[1], 10);
  const low = s.toLowerCase();
  if (/\b(terrible|awful|miserable|worst|very\s+low)\b/.test(low)) return 1;
  if (/\b(sad|down|low|bad|rough|hard|tough|negative)\b/.test(low)) return 2;
  if (/\b(okay|ok|fine|meh|neutral|alright)\b/.test(low)) return 3;
  if (/\b(good|happy|nice|pleasant|positive|content)\b/.test(low)) return 4;
  if (/\b(great|amazing|wonderful|fantastic|excellent|joyful|best)\b/.test(low)) return 5;
  return null;
}

// Strip an explicit mood phrase out of the text so it doesn't pollute the saved entry body.
function stripMoodPhrase(s: string): string {
  return s
    .replace(/[,;]?\s*\b(?:mood|feeling|felt|rating)\s*(?:of|:|=|is|was)?\s*[1-5]\b/gi, "")
    .replace(/[,;]?\s*\b[1-5]\s*\/\s*5\b/gi, "")
    .replace(/[\s,;]+$/, "")
    .trim();
}

// First clause / first ~8 words of the body → a compact title.
function deriveTitle(body: string): string {
  const firstLine = body.split(/\n/)[0].trim();
  const clause = (firstLine.split(/[.!?]/)[0].trim() || firstLine).trim();
  const words = clause.split(/\s+/).filter(Boolean);
  const title = words.slice(0, 8).join(" ");
  return words.length > 8 ? `${title}…` : title || "Untitled entry";
}

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// Resolve an edit/delete target to matching entries: relative-day first ("today"/"yesterday"),
// then title substring, then title+body substring.
export async function resolveTarget(rawTerm: string): Promise<JournalEntry[]> {
  const term = rawTerm.trim().replace(/'s\b/i, "").replace(/[.?!]+$/, "").trim();
  const low = term.toLowerCase();
  const all = await store.listEntries();

  const dayBounds = (offset: number): [number, number] => {
    const s = startOfToday(); s.setDate(s.getDate() + offset);
    return [s.getTime(), s.getTime() + 86_400_000];
  };
  let bounds: [number, number] | null = null;
  if (/\btoday\b/.test(low)) bounds = dayBounds(0);
  else if (/\byesterday\b/.test(low)) bounds = dayBounds(-1);
  if (bounds) {
    const [s, e] = bounds;
    const hits = all.filter((x) => { const t = new Date(x.created_at).getTime(); return t >= s && t < e; });
    if (hits.length) return hits;
  }

  if (!low) return [];
  // Pattern 6 — forgiving resolution over TITLES: exact substring first, then fuzzy token
  // near-match ("the wlak entry" still finds "Long walk by the river"). findNamedMatches does
  // both, in that order, and never invents a match from thin air.
  const named = all.map((e) => ({ name: e.title, e }));
  const hits = findNamedMatches(named, term);
  if (hits.length) return hits.map((h) => h.e);
  // Last resort: substring over title+body. Resolution stays fully on-device.
  return all.filter((x) => `${x.title} ${x.body}`.toLowerCase().includes(low));
}

// The helpful miss: offer the closest titles ("did you mean …?") instead of a dead "couldn't find".
export async function missMessage(term: string): Promise<string> {
  const all = await store.listEntries();
  return notFoundMessage(all.map((e) => ({ name: e.title })), term);
}

function pickList(rows: JournalEntry[]): string {
  return rows.slice(0, 5).map((e) => `• "${e.title}" (${fmtShort(e.created_at)}, mood ${e.mood}/5)`).join("\n");
}

// ── SERVER-FIRST CHAT BRAIN ─────────────────────────────────────────────────────
// The app's action catalog, declared for the server brain. Names MUST stay in lockstep with the
// ChatAction kinds executeAction() can actually run — never list something the app can't execute.
// Param descriptions are written for the LLM: what each means + the expected format.

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.journal.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

/** Exported so the LIVE VOICE call can declare the SAME powers the typed chat has. */
export const CHAT_ACTIONS: { name: string; description: string; params?: Record<string, string> }[] = [
  {
    name: "add_entry",
    description: "Save a new journal entry.",
    params: {
      body: "the entry text, as the user gave it (required)",
      title: "a short title; omit to derive one from the body",
      mood: "mood 1-5 (1=low, 2=down, 3=okay, 4=good, 5=great); omit if the user didn't state one",
      date: "optional date the entry is FOR as YYYY-MM-DD (e.g. they say 'yesterday' or name a day — resolve against CURRENT DATE); omit for today",
    },
  },
  {
    name: "edit_entry",
    description: "Change an existing entry's mood, title, or text. Include only the fields being changed.",
    params: {
      target: "which entry: 'today', 'yesterday', or words from its title (see RECENT ENTRIES)",
      mood: "new mood 1-5 (only when changing the mood)",
      title: "new title (only when renaming)",
      body: "new entry text (only when rewriting the text)",
    },
  },
  {
    name: "delete_entry",
    description: "Delete one entry (the app asks the user to confirm before executing).",
    params: {
      target: "which entry: 'today', 'yesterday', or words from its title (see RECENT ENTRIES)",
    },
  },
  {
    name: "filter",
    description:
      "Search/filter the journal by free text, mood range and/or date window — also how to answer " +
      "'show/find/list/how many' questions. All params optional; combine as needed.",
    params: {
      text: "free text matched against title + body",
      moodMin: "lowest mood 1-5 to include (happy/good entries → 4, great → 5)",
      moodMax: "highest mood 1-5 to include (sad/low entries → 2)",
      since: "start date YYYY-MM-DD, inclusive — resolve relative dates against CURRENT DATE",
      until: "end date YYYY-MM-DD, exclusive",
    },
  },
  {
    name: "reflect",
    description:
      "AI reflection over the recent weeks of journaling (mood trends, cadence, patterns) — " +
      "computed from metadata only. Use for 'reflect', 'how am I doing', 'reflect on my week'.",
  },
  {
    name: "recap",
    description:
      "Answer a question about, or recap, what the user actually WROTE — 'what was on my mind in " +
      "March', 'themes this year', 'what did I write about mom', 'recap June'. Because the user " +
      "explicitly asked about their writing, the app sends the matching entries' text for this one " +
      "answer. Use for any ask ABOUT the content of their entries; use 'filter' when they only want " +
      "to find/list entries, and 'reflect' for the mood/cadence weekly reflection.",
    params: {
      question: "the user's ask, as they phrased it",
      text: "optional topic keyword(s) to pre-filter entries by (matched against title + body)",
      since: "optional start date YYYY-MM-DD, inclusive — resolve relative periods against CURRENT DATE",
      until: "optional end date YYYY-MM-DD, exclusive",
    },
  },
  {
    name: "save_to_cloud",
    description:
      "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when " +
      "they ask to store/save/keep the conversation or a piece of text.",
    params: {
      content_kind: '"conversation" to save this whole chat, or "note" for a piece of text',
      note: 'the text to save when content_kind is "note"',
      destination: 'optional "github" or "gdrive" if the user names one; omit otherwise',
    },
  },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (journal photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// Small metadata-only context so the brain can resolve references ("the walk entry", "yesterday's").
// Titles + moods + dates ONLY — bodies never leave the device.
async function buildContext(): Promise<string> {
  const all = await store.listEntries();
  const recent = [...all]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 40)
    .map((e) => `- "${e.title.slice(0, 60)}" (${e.created_at.slice(0, 10)}, mood ${e.mood}/5)`);
  return (
    `TOTAL ENTRIES: ${all.length}\n` +
    `RECENT ENTRIES (newest first; titles/moods/dates only):\n${recent.join("\n") || "(none yet)"}`
  );
}

// The server sends BOTH `actions` (the array) and back-compat `action`/`params` = the first element.
// Reading only the singular field is the G17 trap — "delete Monday's and Tuesday's" would do half.
interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

// One billed server call: message + action catalog + context + recent turns → { actions[], reply }.
export async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  // Memory window = the server cap (20 turns), not less — "it forgot after a few turns" was G6.
  // noHistory turns (recap answers quoting entry text) are dropped BEFORE the window: quoted
  // journal content transits exactly once, on the explicit ask, never again ambiently.
  const turns = history
    .filter((t) => !t.noHistory)
    .slice(-20)
    .map((t) => ({ role: t.role, content: t.content.slice(0, 800) }));
  const r = await brainCall<ServerChat>("chat", {
    message: text + (await maybeWebContext(text)),
    actions: CHAT_ACTIONS,
    context,
    history: turns,
    scope: JOURNAL_SCOPE,
    assist: true,
  }, onChunk);
  const say = (r.reply ?? "").trim();
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []); // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: say || HELP }] };

  // Map EVERY server action; a per-item failure ("which entry?") becomes a visible none-message,
  // never a silently dropped item.
  const actions: ChatAction[] = [];
  const notes: string[] = [];
  for (const a of raw) {
    const one = await toAction(a.name ?? a.action ?? "", a.params ?? {}, "", text);
    if (one.reply) notes.push(one.reply);
    actions.push(...one.actions);
  }
  const joined = [say, ...notes].filter(Boolean).join("\n");
  return actions.length
    ? { reply: joined, actions }
    : { reply: joined || HELP, actions: [{ kind: "none" }] };
}

// ── loose-param coercion helpers (server params are untyped JSON) ──
const asStr = (v: unknown): string | undefined => {
  if (typeof v === "string") { const s = v.trim(); return s || undefined; }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
};

// Mood 1-5 from a number, a numeric string, or a mood word ("good" → 4) via the local parser.
const asMood = (v: unknown): number | undefined => {
  if (typeof v === "number" && Number.isFinite(v)) {
    const n = Math.round(v);
    return n >= 1 && n <= 5 ? n : undefined;
  }
  if (typeof v === "string") {
    const n = parseInt(v.trim(), 10);
    if (Number.isFinite(n)) return n >= 1 && n <= 5 ? n : undefined;
    return parseMood(v) ?? undefined;
  }
  return undefined;
};

const asDate = (v: unknown): string | undefined => {
  const s = asStr(v);
  return s && Number.isFinite(new Date(s).getTime()) ? s : undefined;
};

const none = (message: string): BrainResponse => ({ reply: "", actions: [{ kind: "none", message }] });
const one = (reply: string, action: ChatAction): BrainResponse => ({ reply, actions: [action] });

// Resolve a loose reference ("today", "the walk entry") to exactly ONE local entry, or a helpful miss.
async function resolveOne(term: string): Promise<{ entry: JournalEntry } | { fail: BrainResponse }> {
  const rows = await resolveTarget(term);
  if (rows.length === 0) return { fail: none(await missMessage(term)) };
  if (rows.length > 1) {
    return { fail: none(`I found ${rows.length} entries matching "${term}" — be more specific:\n${pickList(rows)}`) };
  }
  return { entry: rows[0] };
}

// Coerce the server's loose { action, params } into a typed, locally-resolved ChatAction.
/** Exported for chat/voiceTools.ts — voice must map (name,args) exactly as chat does. */
export async function toAction(
  name: string,
  p: Record<string, unknown>,
  reply: string,
  userText: string,
): Promise<BrainResponse> {
  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (name === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: reply, actions: [{ kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) }] };
  }
  if (name === "library_list") return { reply: reply, actions: [{ kind: "library_list", folder: asStr(p.folder) }] };
  if (name === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: reply, actions: [{ kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` }] };
  }
  if (name === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: reply, actions: [{ kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` }] };
  }
  if (name === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: reply, actions: [{ kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") }] };
  }

  switch (name) {
    case "add_entry": {
      const raw = asStr(p.body) ?? asStr(p.text) ?? asStr(p.title);
      if (!raw) return none(reply || 'What should the entry say? Try "add entry Long walk by the river, mood 4".');
      const body = stripMoodPhrase(raw) || raw;
      const explicit = asMood(p.mood);
      const mood = explicit ?? 3;
      const title = asStr(p.title) ?? deriveTitle(body);
      // Backdating ("journal yesterday's walk"): a YYYY-MM-DD date becomes local NOON of that day,
      // so it lands on the right calendar day in every timezone (UTC midnight would not).
      let created_at: string | undefined;
      const d = asDate(p.date);
      const ymd = d?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymd) created_at = new Date(+ymd[1], +ymd[2] - 1, +ymd[3], 12, 0, 0).toISOString();
      const preface = [reply, explicit == null ? 'Mood set to Okay (3) — say e.g. "mood 4" to change it.' : ""]
        .filter(Boolean).join("\n");
      return one(preface, { kind: "add_entry", title, body, mood, created_at });
    }

    case "edit_entry": {
      const target = asStr(p.target) ?? asStr(p.entry);
      if (!target) return none(reply || "Which entry should I edit — today's, yesterday's, or one by title?");
      const patch: EntryPatch = {};
      const mood = asMood(p.mood);
      if (mood != null) patch.mood = mood;
      const title = asStr(p.title);
      if (title) patch.title = title;
      const body = asStr(p.body);
      if (body) patch.body = body;
      if (Object.keys(patch).length === 0) {
        return none(reply || `What should I change on "${target}" — the mood (1–5), the title, or the text?`);
      }
      const r = await resolveOne(target);
      if ("fail" in r) return r.fail;
      const notes = [
        patch.mood != null ? `mood → ${patch.mood}/5` : "",
        patch.title ? `title → "${patch.title}"` : "",
        patch.body ? "text updated" : "",
      ].filter(Boolean).join(", ");
      return one(reply, { kind: "edit_entry", id: r.entry.id, patch, label: `"${r.entry.title}" (${notes})` });
    }

    case "delete_entry": {
      const target = asStr(p.target) ?? asStr(p.entry) ?? asStr(p.title);
      if (!target) return none(reply || "Which entry should I delete — today's, yesterday's, or one by title?");
      const r = await resolveOne(target);
      if ("fail" in r) return r.fail;
      return one(reply, { kind: "delete_entry", id: r.entry.id, label: `"${r.entry.title}" (${fmtShort(r.entry.created_at)})` });
    }

    case "filter": {
      const f: EntryFilter = {};
      const text = asStr(p.text);
      if (text) f.text = text;
      const moodMin = asMood(p.moodMin);
      if (moodMin != null) f.moodMin = moodMin;
      const moodMax = asMood(p.moodMax);
      if (moodMax != null) f.moodMax = moodMax;
      const since = asDate(p.since);
      if (since) f.since = since;
      const until = asDate(p.until);
      if (until) f.until = until;
      return one(reply, { kind: "filter", filter: f, label: userText });
    }

    case "reflect":
      return one(reply, { kind: "reflect" });

    case "recap": {
      const f: EntryFilter = {};
      const topic = asStr(p.text) ?? asStr(p.topic);
      if (topic) f.text = topic;
      const since = asDate(p.since);
      if (since) f.since = since;
      const until = asDate(p.until);
      if (until) f.until = until;
      const question = asStr(p.question) ?? userText;
      const label = [
        topic ? `about "${topic}"` : "",
        since || until ? `${since ?? "the start"} → ${until ?? "now"}` : "",
      ].filter(Boolean).join(", ");
      return one(reply, { kind: "recap", filter: f, question, label: label || undefined });
    }

    case "save_to_cloud": {
      const kindStr = (asStr(p.content_kind) ?? asStr(p.contentKind) ?? "note").toLowerCase();
      const contentKind = kindStr === "conversation" ? "conversation" as const : "note" as const;
      const destRaw = asStr(p.destination)?.toLowerCase();
      const destination = destRaw === "github" || destRaw === "gdrive" ? destRaw : undefined;
      if (contentKind === "note" && !asStr(p.note)) return none("Tell me what to save and I'll store it.");
      return one(reply, { kind: "save_to_cloud", contentKind, note: asStr(p.note), destination });
    }

    case "backup_save":
      return one(reply, { kind: "backup_save" });
    case "backup_status":
      return one(reply, { kind: "backup_status" });
    case "backup_restore": {
      const prov = asStr(p.provider);
      return one(reply, { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined });
    }
    case "save_local_copy":
      return one(reply, { kind: "save_local_copy" });
    case "import_file":
      return one(reply, { kind: "import_file" });
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(reply || "What should I remember?");
      return one(reply, { kind: "remember", instruction });
    }
    default:
      return none(reply || HELP);
  }
}

// SERVER-FIRST: one billed brain call maps the message to an action; any failure (offline,
// signed out, funds, server trouble) falls back to the free local regex router below.
export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const idx = history.map((t) => t.role).lastIndexOf("user");
  const text = (idx >= 0 ? history[idx].content : "").trim();
  if (!text) return none(HELP);
  // Pre-LLM safety: acute self-harm/crisis phrasing → human support, never the model (no billed call).
  if (hasDistressText(text)) return none(CRISIS_MESSAGE);
  try {
    return await serverChat(history.slice(0, idx), text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return none(e.message);
    }
    return routeMessage(text);
  }
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) ADD ENTRY — anchored so a stray "add"/"write" word elsewhere never triggers it.
  const addM =
    t.match(/^(?:add|create|new|log|record|write|journal)\s+(?:a\s+|an\s+|the\s+|new\s+|my\s+)?(?:journal\s+)?(?:entry|note)\b[:\-\s]*([\s\S]*)$/i)
    || t.match(/^(?:add|log|record|write|journal|note)\b[:\-\s]+([\s\S]+)$/i);
  if (addM) {
    const payload = (addM[1] ?? "").trim();
    if (!payload) {
      return none('What should the entry say? Try "add entry Long walk by the river, mood 4".');
    }
    const explicitMood = parseMood(payload);
    const mood = explicitMood ?? 3;
    const body = stripMoodPhrase(payload) || payload;
    const title = deriveTitle(body);
    const preface = explicitMood == null ? "Mood set to Okay (3) — say e.g. \"mood 4\" to change it." : "";
    return one(preface, { kind: "add_entry", title, body, mood });
  }

  // 2) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|discard|trash)\s+(?:the\s+)?(?:journal\s+)?(?:entry\s+)?(?:for\s+|about\s+|titled\s+|called\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await resolveTarget(term);
    if (rows.length === 0) return none(await missMessage(term));
    if (rows.length > 1) {
      return none(`I found ${rows.length} entries matching "${term}" — be more specific:\n${pickList(rows)}`);
    }
    const e = rows[0];
    return one("", { kind: "delete_entry", id: e.id, label: `"${e.title}" (${fmtShort(e.created_at)})` });
  }

  // 3) EDIT — set mood / rename. Single-match resolution only.
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 4) REFLECT — the one billed action (reuses the live /reflect endpoint).
  if (/\breflect(?:ion)?\b/i.test(low)
    || /\b(?:recap|summar(?:y|ise|ize)|overview|review)\b[\s\S]*\bweek\b/i.test(low)
    || /\bhow\s+(?:am|was)\s+i\s+doing\b/i.test(low)
    || /\bmy\s+week\b/i.test(low)) {
    return one("", { kind: "reflect" });
  }

  // 5) FILTER / SEARCH — query-shaped → local filter over the entry list.
  if (QUERY_RE.test(low)) {
    return one("", { kind: "filter", filter: parseFilter(t), label: t });
  }

  // 6) Unclear → a short clarifying nudge.
  return none(HELP);
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string): Promise<BrainResponse | null> {
  // "set/change/update X('s) mood to N|word" · "mark/rate X as good"
  let m = t.match(/^(?:set|change|update)\s+(.+?)(?:'s)?\s+mood\s+to\s+(.+)$/i)
    || t.match(/^(?:mark|rate)\s+(.+?)\s+as\s+(.+)$/i);
  if (m) {
    const mood = parseMood(m[2]);
    if (mood == null) {
      return none(`What mood (1–5) should I set? e.g. "set ${m[1].trim()}'s mood to 4".`);
    }
    return resolveEdit(m[1], { mood }, `mood → ${mood}/5`);
  }

  // "rename/retitle X to Y"
  m = t.match(/^(?:rename|retitle|title)\s+(.+?)\s+to\s+(.+)$/i);
  if (m) {
    const title = m[2].trim().replace(/["']/g, "").replace(/[.?!]+$/, "").trim();
    if (!title) return null;
    return resolveEdit(m[1], { title }, `title → "${title}"`);
  }

  return null;
}

async function resolveEdit(term: string, patch: EntryPatch, note?: string): Promise<BrainResponse> {
  const rows = await resolveTarget(term);
  if (rows.length === 0) return none(await missMessage(term.trim()));
  if (rows.length > 1) {
    return none(`I found ${rows.length} matching "${term.trim()}" — be more specific:\n${pickList(rows)}`);
  }
  const e = rows[0];
  const label = note ? `"${e.title}" (${note})` : `"${e.title}"`;
  return one("", { kind: "edit_entry", id: e.id, patch, label });
}

// Build a client-side EntryFilter from a query string (mood range + date window + free text).
function parseFilter(t: string): EntryFilter {
  const low = t.toLowerCase();
  const f: EntryFilter = {};

  // mood range
  let m = low.match(/mood\s*(?:>=|>|above|over|at\s+least|min(?:imum)?)\s*([1-5])/);
  if (m) f.moodMin = parseInt(m[1], 10);
  m = low.match(/mood\s*(?:<=|<|below|under|at\s+most|max(?:imum)?)\s*([1-5])/);
  if (m) f.moodMax = parseInt(m[1], 10);
  if (f.moodMin == null && f.moodMax == null) {
    m = low.match(/mood\s*(?:of|=|:|is|was)?\s*([1-5])\b/);
    if (m) { f.moodMin = parseInt(m[1], 10); f.moodMax = f.moodMin; }
  }
  // mood words (only if no numeric mood was given)
  if (f.moodMin == null && f.moodMax == null) {
    if (/\b(great|amazing|wonderful|joyful|best)\b/.test(low)) f.moodMin = 5;
    else if (/\b(happy|good|nice|positive|up|content)\b/.test(low)) f.moodMin = 4;
    else if (/\b(sad|down|low|bad|rough|hard|tough|negative)\b/.test(low)) f.moodMax = 2;
  }

  // date window
  const today = startOfToday();
  const iso = (d: Date) => d.toISOString();
  const shift = (base: Date, days: number) => { const d = new Date(base); d.setDate(d.getDate() + days); return d; };
  if (/\btoday\b/.test(low)) { f.since = iso(today); }
  else if (/\byesterday\b/.test(low)) { f.since = iso(shift(today, -1)); f.until = iso(today); }
  else if (/\bthis\s+week\b/.test(low)) { f.since = iso(shift(today, -7)); }
  else if (/\blast\s+week\b/.test(low)) { f.since = iso(shift(today, -14)); f.until = iso(shift(today, -7)); }
  else if (/\bthis\s+month\b/.test(low)) { f.since = iso(new Date(today.getFullYear(), today.getMonth(), 1)); }
  else if (/\blast\s+month\b/.test(low)) {
    f.since = iso(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    f.until = iso(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  // free text: "about X" / "mentioning X" / "containing X" / "matching X"
  const tm = t.match(/\b(?:about|mentioning|mentions?|containing|contains?|matching|regarding)\s+["']?(.+?)["']?\s*$/i);
  if (tm) {
    let term = tm[1].trim().replace(/\b(today|yesterday|this|last|week|month|year)\b/gi, "").trim();
    if (term.length >= 2) f.text = term;
  }
  return f;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
